import axios from "axios";
import useUserStore from "../stores/user-store";
import { resolveApiBaseUrl } from "./api-base-url";
import { isAuthFailure, isRefreshAuthFailure, refreshAccessToken, requestInteractiveLogin } from "./auth-session";
import { applyCsrfHeader } from "./csrf";
import { toApiError } from "./http-clients";

const api = axios.create({
    baseURL: resolveApiBaseUrl(),
    withCredentials: true
});

api.interceptors.request.use((config) => {
    applyCsrfHeader(config);

    const token = useUserStore.getState().getToken();

    if (token && !config.__skipAuthHeader) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error?.config;
        const normalizedError = toApiError(error);

        if (
            !isAuthFailure(normalizedError) ||
            !originalRequest ||
            originalRequest.__isRetryRequest ||
            originalRequest.__skipAuthHandlers ||
            !useUserStore.getState().loggedIn
        ) {
            return Promise.reject(normalizedError);
        }

        originalRequest.__isRetryRequest = true;

        try {
            await refreshAccessToken();
        } catch (refreshError) {
            const normalizedRefreshError = toApiError(refreshError);

            if (!isRefreshAuthFailure(normalizedRefreshError) || originalRequest.__skipReauthModal) {
                return Promise.reject(normalizedRefreshError);
            }

            await requestInteractiveLogin();
        }

        const latestToken = useUserStore.getState().getToken();
        originalRequest.headers = originalRequest.headers ?? {};

        if (latestToken) {
            originalRequest.headers.Authorization = `Bearer ${latestToken}`;
        }

        return api(originalRequest);
    }
);

export default api;
